import { BrainIcon, PlayIcon } from '@/components/icons';
import { PatientIdModal } from '@/components/portal/PatientIdModal';
import { useNote } from '@/context/NoteProvider';
import { Box, Typography } from '@mui/material';
import { useState } from 'react';
import { audioCardStyles } from '../../styles';
import { MicrophoneSelector } from '../../MicrophoneSelector';

interface InitialStateProps {
  title: string;
  subtitle: string;
  onStartRecording: () => void;
  disabled?: boolean;
  onEnsureMicPermission?: () => Promise<boolean>;
  micError?: string | null;
  selectedDeviceId: string | null;
  onDeviceChange: (deviceId: string) => void;
}

const InitialState: React.FC<InitialStateProps> = ({
  title,
  subtitle,
  onStartRecording,
  disabled = false,
  onEnsureMicPermission,
  micError,
  selectedDeviceId,
  onDeviceChange,
}) => {
  const [isModalOpen, setIsModalOpen] = useState(false);
  const { patientId, setPatientId } = useNote();

  const handlePlayClick = async () => {
    if (disabled) return;
    if (onEnsureMicPermission) {
      const ok = await onEnsureMicPermission();
      if (!ok) return; // Do not open modal; error will be shown by parent
    }
    setIsModalOpen(true);
  };

  const handlePatientIdSubmit = (_patientId: string) => {
    setPatientId(_patientId);
    setIsModalOpen(false);
    onStartRecording();
  };

  const handleSkip = () => {
    setIsModalOpen(false);
    onStartRecording();
  };

  const handleModalClose = () => {
    setIsModalOpen(false);
  };

  return (
    <Box sx={{ position: 'relative' }}>
      <Box sx={audioCardStyles.iconContainer}>
        <BrainIcon width={22} height={22} />
      </Box>

      <Typography variant="h5" sx={audioCardStyles.title}>
        {title}
      </Typography>

      <Typography variant="body1" sx={audioCardStyles.subtitle}>
        {subtitle}
      </Typography>

      <Box
        sx={{
          ...audioCardStyles.playButton,
          opacity: disabled ? 0.5 : 1,
          cursor: disabled ? 'not-allowed' : 'pointer',
          pointerEvents: disabled ? 'none' : 'auto',
          '& svg': {
            width: { xs: '80px', lg: '108px' },
            height: { xs: '80px', lg: '108px' },
          },
        }}
        onClick={handlePlayClick}
      >
        <PlayIcon />
      </Box>

      <PatientIdModal
        isOpen={isModalOpen}
        onClose={handleModalClose}
        onSubmit={handlePatientIdSubmit}
        onSkip={handleSkip}
        initialValue={patientId ?? ''}
      />
      {micError && (
        <Box sx={audioCardStyles.micErrorContainer}>
          <Typography variant="body2" sx={audioCardStyles.micErrorText}>
            {micError}
          </Typography>
        </Box>
      )}

      <MicrophoneSelector
        selectedDeviceId={selectedDeviceId}
        onDeviceChange={onDeviceChange}
      />
    </Box>
  );
};

export { InitialState };
